import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { util } from '../../../../classes/util';
import { ESRI } from '../../../../esri-loader-util';
import { InputConverter } from '../../../../inputConverter';
import { ILoggingService } from '../../../../services/interfaces/ILoggingService';
import { IUserSettingsService } from '../../../../services/interfaces/IUserSettingsService';
import { LayerService } from '../../../../services/layer.service';
import { MeasureState } from '../../../../types';
import { BaseComponent } from '../../../base.component';

import DistanceMeasurementConfiguration, { DistanceMeasureConfigurationConverter } from './distance.config';

const initilizeDebounceTime = 50;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-distance-measurement-ng',
    styleUrls: ['../../../sharedStyles/base.scss', './distance.component.scss'],
    templateUrl: './distance.component.html'
})
/**
 * Component implementing the ESRI DistanceMeasurement2D tool
 *
 * Usage:
 *
 * ```html
 * <mc-distance-measurement [distanceMeasureConfig]="distanceMeasureConfig"></mc-distance-measurement>
 * ```
 *
 * ```typescript
 *
 * ```
 *
 * See the [[__esri.DistanceMeasurement2DProperties]] for more details.
 *
 */
export class DistanceComponent extends BaseComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
    distanceMeasurement2D: __esri.DistanceMeasurement2D;
    private mapView: __esri.MapView;

    /**
     * Class that determines which icon is used for the button.
     *
     * @Default 'esri-icon-minus'
     */
    @Input() iconClass?: string;

    /**
     * Overall state of the measure component. Values: true = User may draw measure line on map, false = tool is inactive.
     */
    @Input() @InputConverter() set active(value: boolean) {
        this._active = value;
        value ? this.activate() : this.deactivate();
        this.activeChange.emit(value);
    }
    get active(): boolean { return this._active; }
    @Output() readonly activeChange: EventEmitter<boolean> = new EventEmitter();
    private _active: boolean = false;

    /**
     * Boolean flag indicating if the measurement should be turned into a layer automatically when measure is completed
     *
     * @Default false
     */
    @Input() set autolayer(value: boolean) {
        this._autolayer = value;
        if (this._autolayer &&
            this.distanceMeasurement2D.viewModel.measurement &&
            !(this.distanceMeasurement2D.viewModel as any).active) {
            this.addAsLayer(this.distanceMeasurement2D.viewModel.measurement, this.distanceMeasurement2D.viewModel.measurementLabel);
            this.distanceMeasurement2D.viewModel.start();
        }
    }
    get autolayer(): boolean { return this._autolayer; }
    private _autolayer: boolean = false;

    /**
     * Object containing default settings for this component.
     */
    @Input()
    @InputConverter(DistanceMeasureConfigurationConverter)
    distanceMeasurementConfigs: DistanceMeasurementConfiguration = new DistanceMeasurementConfiguration();

    /**
     * Measure component will find users default values and give those priority over other settings.
     */
    @Input() includeUserDefault = true;

    /**
     * The threshold distance used by the "auto" mode to switch between planar and geodesic linear computations. The threshold is measured in meters.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-DistanceMeasurement2D-DistanceMeasurement2DViewModel.html#geodesicDistanceThreshold)
     *
     */
    @Input() set geodesicDistanceThreshold(value: number) {
        this._geodesicDistanceThreshold = value;
        if (this.distanceMeasurement2D) {
            this.distanceMeasurement2D.viewModel.geodesicDistanceThreshold = value;
        }
    }
    get geodesicDistanceThreshold(): number {
        return (this._geodesicDistanceThreshold = this.distanceMeasurement2D ? this.distanceMeasurement2D.viewModel.geodesicDistanceThreshold : this._geodesicDistanceThreshold);
    }
    private _geodesicDistanceThreshold: number;

    /**
     * Display the Measure interface while drawing measurement.
     *
     * When set to false the user will be able to draw the distance line but not start a new distance line.
     *
     * @Default true
     *
     */
    @Input() showDialog = true;

    /**
     * State of the measurement tool. Values: 'disabled', 'ready', 'measuring', 'measured'
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-DistanceMeasurement2D-DistanceMeasurement2DViewModel.html#state)
     */
    set state(value: MeasureState) {
        this._state = value;
        this.stateChange.emit(value);
    }
    get state(): MeasureState { return this._state; }
    @Output() readonly stateChange: EventEmitter<MeasureState> = new EventEmitter();
    private _state: MeasureState = 'disabled';

    /**
     * Unit system (imperial, metric) or specific unit used for displaying the distance values.  **Possible Values:** metric | imperial | inches | feet | us-feet | yards | miles | nautical-miles | meters | kilometers
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-DistanceMeasurement2D-DistanceMeasurement2DViewModel.html#unit)
     *
     * @Default imperial
     */
    @Input() set unit(value: __esri.SystemOrLengthUnit) {
        this._unit = value;
        if (this.distanceMeasurement2D) {
            this.distanceMeasurement2D.viewModel.unit = value;
        }
    }
    get unit(): __esri.SystemOrLengthUnit {
        return (this._unit = this.distanceMeasurement2D ? this.distanceMeasurement2D.viewModel.unit : this._unit);
    }
    private _unit: __esri.SystemOrLengthUnit;

    /**
     * List of available units and unit systems (imperial, metric) for displaying the distance values. By default, the following units are included: `metric`, `imperial`, `inches`, `feet`, `us-feet`, `yards`, `miles`, `nautical-miles`, `meters`, `kilometers`.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-DistanceMeasurement2D-DistanceMeasurement2DViewModel.html#unitOptions)
     */
    @Input() set unitOptions(value: __esri.SystemOrLengthUnit[]) {
        this._unitOptions = value;
        if (this.distanceMeasurement2D) {
            this.distanceMeasurement2D.viewModel.unitOptions = value;
        }
    }
    get unitOptions(): __esri.SystemOrLengthUnit[] {
        return (this._unitOptions = this.distanceMeasurement2D ? this.distanceMeasurement2D.viewModel.unitOptions : this._unitOptions);
    }
    private _unitOptions: __esri.SystemOrLengthUnit[];

    @Output() readonly onmeasure: EventEmitter<__esri.DistanceMeasurement2DViewModel> = new EventEmitter();

    @ViewChild('body') body: ElementRef<any>;

    constructor(
        _vcr: ViewContainerRef,
        private readonly layerService: LayerService,
        @Inject('LoggingService') public LoggingService: ILoggingService,
        @Inject('UserSettingsService') public UserSettingsService: IUserSettingsService
    ) {
        super(_vcr);
        this.registerEvent('stateChange', this.stateChange);
        this.registerEvent('activeChange', this.activeChange);
        this.registerEvent('onmeasure', this.onmeasure);
    }

    async ngOnInit() {
        try {
            // Handle defaults
            this.setupAttributeDefaults(this.distanceMeasurementConfigs);
            if (this.includeUserDefault) {
                await this.setupUserDefault();
            }
            this.setProperties(this.distanceMeasurementConfigs);
            await this.setMapViewRef();
        }
        catch (err) {
            this.readyProblem(err);
            throw err;
        }

    }

    async ngAfterViewInit() {
        this.isReady();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.active && !changes.active.firstChange) {
            changes.active.currentValue ? this.activate() : this.deactivate();
            this.activeChange.emit(changes.active.currentValue);
        }
    }

    async ngOnDestroy() {
        if (this.distanceMeasurement2D) {
            this.distanceMeasurement2D.destroy();
            this.distanceMeasurement2D = null;
        }
        this.distanceMeasurementConfigs = null;
    }

    private initializing = false;
    private readonly listners: IHandle[] = [];
    /**
     * Make the distance measurement tool active/shown.
     */
    private async activate() {
        try {
            if (!this.distanceMeasurement2D) {
                this.initializing = true;
                const stopInitialCheck = util.debounce(() => { this.initializing = false; }, initilizeDebounceTime);
                this.listners.push(this.layerService.mapRef.allLayers.on('before-add', (event) => {
                    if (this.initializing && event.item.listMode === 'hide') {
                        event.item.set('hideInLegend', true);
                        event.item.set('distanceMeasureToolGeneratedLayer', true);
                        stopInitialCheck();
                    }
                }));
                this.listners.push(this.layerService.mapRef.allLayers.on('after-remove', (event) => {
                    if (event.item.get<boolean>('distanceMeasureToolGeneratedLayer')) {
                        this.initializing = true;
                    }
                }));

                this.distanceMeasurement2D = await this._createDistanceMeasurement2D(this.distanceMeasurementConfigs);
                this.distanceMeasurement2D.viewModel.start();
            }
        }
        catch (err) {
            this.readyProblem(err);
            throw err;
        }
    }

    /**
     * Make the distance measurement tool inactive/hidden.
     */
    private deactivate(): void {
        if (this.distanceMeasurement2D) {
            this.distanceMeasurement2D.destroy();
            this.distanceMeasurement2D = null;
        }
        while (this.listners.length) {
            this.listners.pop().remove();
        }
    }

    /**
     * Create a DistanceMeasurement2D widgit and return it to caller.
     */
    private async _createDistanceMeasurement2D(config: DistanceMeasurementConfiguration) {

        // Create a temporary <div> for the widget, it will remove this when it is destroyed.
        const newElement = this.body.nativeElement.appendChild(document.createElement('div'));

        // Create tool
        const DistanceMeasurement2D = await ESRI.DistanceMeasurement2D;
        const distanceMeasurement2D = new DistanceMeasurement2D({
            container: newElement,
            viewModel: {
                unit: config.unit,
                unitOptions: config.unitOptions,
                geodesicDistanceThreshold: config.geodesicDistanceThreshold
            }
        } as any);
        distanceMeasurement2D.view = this.mapView;

        this.state = 'ready'; // Widget doesn't call the watcher for its first value; manually set our state

        // Add watchers
        distanceMeasurement2D.watch('viewModel.state', (value) => {
            this.state = value;
        });
        distanceMeasurement2D.watch('viewModel.active', async (newValue, oldValue) => {
            if (!newValue && oldValue) {
                this.onmeasure.emit(distanceMeasurement2D.viewModel);
                this.state = 'measured'; // ESRI component has code for 'measured' state but does not yet apply it? Manually set for now.
                if (this.autolayer) {
                    await this.addAsLayer(distanceMeasurement2D.viewModel.measurement, distanceMeasurement2D.viewModel.measurementLabel);
                    this.distanceMeasurement2D.viewModel.start();
                }
            }
        });
        return distanceMeasurement2D;
    }

    /**
     * Add completed measure geometry as a layer to the map
     */
    async addAsLayer(measurement: __esri.DistanceMeasurement2DViewModelMeasurement, label: string) {
        let Graphic: __esri.GraphicConstructor;
        let GraphicsLayer: __esri.GraphicsLayerConstructor;
        [Graphic, GraphicsLayer] = await Promise.all([ESRI.Graphic, ESRI.GraphicsLayer]);
        const graphic = new Graphic({
            geometry: measurement.geometry,
            symbol: {
                type: 'simple-line',
                color: [0, 128, 255],
                width: 3
            }
        } as __esri.GraphicProperties);
        const layer = new GraphicsLayer({
            title: `Distance: ${label}`,
            graphics: [graphic]
        });
        layer.set('measureLayer', 'distance');
        this.mapView.map.add(layer);
    }

    /**
     * Apply defaults passed in through attributes of this component.
     */
    private setupAttributeDefaults(config: DistanceMeasurementConfiguration) {
        ['label', 'toolTip', 'icon', 'color', 'geodesicDistanceThreshold', 'mode', 'unit', 'unitOptions'].forEach((prop) => {
            if (typeof this[prop] !== 'undefined') {
                config[prop] = this[prop];
            }
        });
    }

    /**
     * Apply user specific defaults for this components behavior.
     */
    private async setupUserDefault() {
        if (this.includeUserDefault) {
            // FIXME: Instead of using default apply User setttings here.
            // const userDistanceMeasureConfiguration = await this.UserSettingsService.GetDistanceMeasurementSettings();
            const userDistanceMeasureConfiguration = new DistanceMeasurementConfiguration();
            if (userDistanceMeasureConfiguration) {
                Object.assign(this.distanceMeasurementConfigs, userDistanceMeasureConfiguration);
            }
        }
    }

    /**
     * Set undefined primary properties of this component with user/config properties.
     */
    private setProperties(config: DistanceMeasurementConfiguration) {
        ['label', 'toolTip', 'icon', 'color'].forEach((prop) => {
            if (typeof this[prop] === 'undefined' && (typeof config[prop] !== 'undefined')) {
                this[prop] = config[prop];
            }
        });
    }

    /**
     * Set private reference to this components mapView.
     */
    private async setMapViewRef() {
        if (!this.mapView) {
            const parent = await this.getClosestAncestor('mc-map');
            if (parent) {
                await parent.ready;
                this.mapView = parent.mapView;
            } else {
                this.readyProblem('Map View Reference not supplied and reference from parent element could not be retrieved.');
            }
        }
    }
}
